import { t } from '@lingui/core/macro';
import type { CarFleetViewRecord, UserGroupAddressRecord, UserRecord } from '@navio/nsdk';
import { PersonalInfoReviewSection } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { noddiToast } from 'noddi-ui-web';
import { useCallback, useEffect, useState } from 'react';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { useConfirmPersonalInfoActions } from '../../stores/confirmPersonalInfoStore';
import { ConfirmAddressesDialog } from './ConfirmAddressesDialog';
import { ConfirmCarsDialog } from './ConfirmCarsDialog';
import { ConfirmContactDialog } from './ConfirmContactDialog';
import { buildReviewPlan } from './reviewPlan';
import { PERSONAL_INFO_REVIEW_MUTATION_KEY } from './types';
import { useConfirmPersonalInfoMutation } from './useConfirmPersonalInfoMutation';

type Props = {
  userId: number;
  personalUserGroupId: number;
  /** The dialogs this brand asks for, in the order they are shown. The backend stores them in dialog order. */
  sections: PersonalInfoReviewSection[];
};

/**
 * Loads what the brand's review needs, then hands off to the runner.
 *
 * Only what the brand asks about is fetched — a brand that never shows the cars
 * dialog should not pay for the fleet lookup on every post-login landing.
 */
export const ConfirmPersonalInfoFlow = ({ userId, personalUserGroupId, sections }: Props) => {
  const asksAboutCars = sections.includes(PersonalInfoReviewSection.CARS);
  const asksAboutAddresses = sections.includes(PersonalInfoReviewSection.ADDRESSES);

  const { data: user, error: userError } = noddiAsync.useGet({
    type: URLKeys.getAccount,
    input: { userId }
  });
  // Deliberately NOT scoped to the current brand. `wheelStorage` is computed for
  // the brand's departments, while the backend refuses to end ownership if the
  // wheels are stored anywhere (`car_has_wheels_in_storage`). Scoped, a car whose
  // wheels sit under another brand showed no badge, offered Remove, and the
  // removal failed. Removal ends ownership globally, so the list has to be global
  // too or it disagrees with its own action.
  const { data: cars, error: carsError } = noddiAsync.useGet({
    type: URLKeys.getPaginatedFleetViewCars,
    input: { userGroupIds: [personalUserGroupId] },
    queryConfig: { enabled: asksAboutCars }
  });
  const { data: userGroupAddresses, error: userGroupAddressesError } = noddiAsync.useGet({
    type: URLKeys.getUserGroupAddresses,
    input: { userGroupId: personalUserGroupId },
    queryConfig: { enabled: asksAboutAddresses }
  });

  const isLoaded = !!user && (!asksAboutCars || !!cars) && (!asksAboutAddresses || !!userGroupAddresses);
  const hasLoadError =
    (!!userError && !user) || (!!carsError && !cars) || (!!userGroupAddressesError && !userGroupAddresses);

  // Nothing is shown until the registered values are loaded — an empty prompt asks the user to confirm
  // nothing — and a failed lookup skips the prompt rather than blocking the page behind an error dialog.
  if (hasLoadError || !isLoaded) {
    return null;
  }

  return (
    <ReviewRunner
      userId={userId}
      personalUserGroupId={personalUserGroupId}
      sections={sections}
      user={user}
      cars={cars}
      userGroupAddresses={userGroupAddresses}
    />
  );
};

type RunnerProps = Props & {
  user: UserRecord;
  cars: { count: number; results: CarFleetViewRecord[] } | undefined;
  userGroupAddresses: { count: number; results: UserGroupAddressRecord[] } | undefined;
};

/**
 * Walks the brand's dialogs: one plain dialog per step, one Confirm each, Remind
 * me later always present. Contact edits and car/address changes are saved by
 * their own endpoints as they happen; only the last Confirm stamps the review, so
 * quitting half way keeps the edits and still asks again next time.
 *
 * The plan is a snapshot taken when this mounts, not a value derived every
 * render. A refetch landing mid-flow must not pull a dialog off the screen or
 * silently shorten the review, and completion has to be something the customer
 * did rather than something a re-render noticed.
 */
const ReviewRunner = ({ userId, personalUserGroupId, sections, user, cars, userGroupAddresses }: RunnerProps) => {
  const { markPromptHandled } = useConfirmPersonalInfoActions();
  const { currentBrand } = useCurrentBrand();

  const [plan] = useState(() => buildReviewPlan({ sections, cars, userGroupAddresses }));
  const [stepIndex, setStepIndex] = useState(0);

  // Remind me later postpones server-side rather than only for this browser session — up to 30
  // days, capped by the brand's own review interval, which is why it carries the brand. The session
  // flag still runs regardless of the result: a failed snooze must not trap the user in the prompt.
  const { mutate: snoozePrompt } = noddiAsync.usePost({
    type: URLKeys.postSnoozePersonalInfoPrompt,
    queryConfig: { mutationKey: PERSONAL_INFO_REVIEW_MUTATION_KEY }
  });
  const { mutate: confirmPersonalInfo } = useConfirmPersonalInfoMutation({ userId });

  const markHandled = useCallback(() => markPromptHandled(userId), [markPromptHandled, userId]);

  const finish = () => {
    noddiToast.success(t`Thanks! Your details are up to date.`);
    markHandled();
  };

  // Nothing this brand asks for applies to this customer, so there is no
  // dialog to show and nothing to record. Standing down is a store write, not a
  // request, so running it from an effect is safe and idempotent.
  const hasNothingToAsk = plan.steps.length === 0;
  useEffect(() => {
    if (hasNothingToAsk) {
      markHandled();
    }
  }, [hasNothingToAsk, markHandled]);

  const completeReview = () => {
    // A review missing a section we could not show in full is not a complete
    // one. Stand down for the session without stamping so it is asked again,
    // rather than buying the brand's whole interval of silence on values the
    // customer never saw.
    if (!plan.isComplete) {
      markHandled();
      return;
    }
    confirmPersonalInfo(
      {},
      {
        onSuccess: finish,
        // The customer answered everything; a failed stamp must not hold them
        // hostage in a prompt with nothing left to show. It comes back next
        // session.
        onError: markHandled
      }
    );
  };

  const advance = () => {
    if (stepIndex + 1 >= plan.steps.length) {
      completeReview();
      return;
    }
    setStepIndex(stepIndex + 1);
  };

  const skipPrompt = () => {
    snoozePrompt({ brandDomain: currentBrand }, { onSettled: markHandled });
  };

  const activeStep = plan.steps[stepIndex];

  if (activeStep === PersonalInfoReviewSection.CONTACT) {
    return <ConfirmContactDialog user={user} onSaved={advance} onSkip={skipPrompt} />;
  }

  if (activeStep === PersonalInfoReviewSection.CARS && cars) {
    return (
      <ConfirmCarsDialog
        userGroupId={personalUserGroupId}
        cars={cars.results}
        onConfirmed={advance}
        onSkip={skipPrompt}
      />
    );
  }

  if (activeStep === PersonalInfoReviewSection.ADDRESSES && userGroupAddresses) {
    return (
      <ConfirmAddressesDialog
        userGroupId={personalUserGroupId}
        userGroupAddresses={userGroupAddresses.results}
        onConfirmed={advance}
        onSkip={skipPrompt}
      />
    );
  }

  return null;
};
